// Currency formatting lives in ./currency; re-exported here because most
// components import it alongside the data helpers.
export { formatCurrency, formatCurrencyCompact } from './currency';

export const TIME_RANGES = [
  { value: '1month', label: 'Last Month', shortLabel: '1M' },
  { value: '3months', label: 'Last 3 Months', shortLabel: '3M' },
  { value: '6months', label: 'Last 6 Months', shortLabel: '6M' },
  { value: '12months', label: 'Last 12 Months', shortLabel: '12M' },
  { value: 'all', label: 'All Time', shortLabel: 'All' }
];

export const getTimeRangeLabel = (range) => {
  const match = TIME_RANGES.find((entry) => entry.value === range);
  return match ? match.label : String(range || '');
};

// Parse an invoice period ("2025-01" or "January 2025") into a comparable date
export const parseMonthPeriod = (periodStr) => {
  if (!periodStr) return new Date(0);

  const value = String(periodStr).trim();

  if (/^\d{4}-\d{2}$/.test(value)) {
    return new Date(`${value}-01`);
  }

  if (value.length <= 32) {
    const firstSpaceIndex = value.indexOf(' ');
    if (firstSpaceIndex > 0) {
      const monthPart = value.slice(0, firstSpaceIndex);
      const yearPart = value.slice(firstSpaceIndex + 1);
      const year = parseInt(yearPart, 10);

      if (!isNaN(year) && yearPart.length === 4) {
        const monthNames = ["january", "february", "march", "april", "may", "june",
                           "july", "august", "september", "october", "november", "december"];
        const month = monthNames.indexOf(monthPart.toLowerCase());
        if (month !== -1) {
          return new Date(year, month, 1);
        }
      }
    }
  }

  return new Date(value);
};

// Filter invoices by time range
export const filterInvoicesByTimeRange = (invoices, selectedRange) => {
  if (!invoices || invoices.length === 0) return [];
  
  const mostRecentInvoice = invoices.reduce((latest, current) => {
    const currentDate = new Date(current.invoice_period || current.created_at || 0);
    const latestDate = new Date(latest.invoice_period || latest.created_at || 0);
    return currentDate > latestDate ? current : latest;
  }, invoices[0]);
  
  const mostRecentDate = mostRecentInvoice 
    ? new Date(mostRecentInvoice.invoice_period || mostRecentInvoice.created_at) 
    : new Date();
  
  const filterDate = new Date();
  
  switch (selectedRange) {
    case '1month':
      return invoices.filter(invoice => {
        const invoiceDate = new Date(invoice.invoice_period || invoice.created_at || 0);
        return invoiceDate.getFullYear() === mostRecentDate.getFullYear() && 
               invoiceDate.getMonth() === mostRecentDate.getMonth();
      });
    case '3months':
      filterDate.setMonth(filterDate.getMonth() - 4);
      break;
    case '6months':
      filterDate.setMonth(filterDate.getMonth() - 7);
      break;
    case '12months':
      filterDate.setMonth(filterDate.getMonth() - 13);
      break;
    case 'all':
      filterDate.setFullYear(2000);
      break;
    default:
      filterDate.setMonth(filterDate.getMonth() - 6);
  }
  
  if (selectedRange !== '1month') {
    return invoices.filter(invoice => {
      const dateToCheck = new Date(invoice.invoice_period || invoice.created_at || 0);
      return !isNaN(dateToCheck.getTime()) && dateToCheck >= filterDate;
    });
  }
  
  return [];
};

// Filter line items by time range
export const filterLineItemsByTimeRange = (lineItems, selectedRange) => {
  if (!lineItems || lineItems.length === 0) return [];
  
  // For "all" time range, return all items
  if (selectedRange === 'all') {
    return lineItems;
  }
  
  const filterDate = new Date();
  
  // Calculate filter date based on selected range
  switch (selectedRange) {
    case '1month':
      filterDate.setMonth(filterDate.getMonth() - 2);
      break;
    case '3months':
      filterDate.setMonth(filterDate.getMonth() - 4);
      break;
    case '6months':
      filterDate.setMonth(filterDate.getMonth() - 7);
      break;
    case '12months':
      filterDate.setMonth(filterDate.getMonth() - 13);
      break;
    default:
      filterDate.setMonth(filterDate.getMonth() - 7); // Default to 6 months
  }
  
  return lineItems.filter(item => {
    // Try multiple date fields that might be in the CSV
    let itemDate;
    
    // Find the most reliable date field
    if (item.invoice_period) {
      // Handle YYYY-MM format
      if (/^\d{4}-\d{2}$/.test(item.invoice_period)) {
        itemDate = new Date(`${item.invoice_period}-01`);
      } else {
        // Try to parse other formats
        itemDate = new Date(item.invoice_period);
      }
    } else if (item.date) {
      itemDate = new Date(item.date);
    } else if (item.start) {
      itemDate = new Date(item.start);
    } else if (item.created_at) {
      itemDate = new Date(item.created_at);
    } else if (item.invoice_date) {
      itemDate = new Date(item.invoice_date);
    }
    
    // If no valid date found, include the item by default
    if (!itemDate || isNaN(itemDate.getTime())) {
      return true;
    }
    
    return itemDate >= filterDate;
  });
};

// Extract monetary value from line item (negative values are discounts)
export const extractMonetaryValue = (item) => {
  // For USD field, clean and handle the string values (including negative values)
  if ('USD' in item) {
    // If it's a string like "$18.00" or "-$1,779.55", clean it up
    if (typeof item.USD === 'string') {
      // Remove $ and any other non-numeric characters except decimal point and minus sign
      const cleaned = item.USD.replace(/[^\d.-]/g, '');
      const value = parseFloat(cleaned);
      if (!isNaN(value)) {
        return value; // This will be negative for discounts
      }
    } else if (typeof item.USD === 'number') {
      return item.USD;
    }
  }
  
  // Check other common field names (allow negative values)
  if ('amount' in item) {
    const amount = parseFloat(item.amount);
    if (!isNaN(amount)) return amount;
  }
  if ('cost' in item) {
    const cost = parseFloat(item.cost);
    if (!isNaN(cost)) return cost;
  }
  if ('price' in item) {
    const price = parseFloat(item.price);
    if (!isNaN(price)) return price;
  }
  if ('charge' in item) {
    const charge = parseFloat(item.charge);
    if (!isNaN(charge)) return charge;
  }
  
  // IMPORTANT: Do NOT use invoice_amount as a fallback
  // This is the entire invoice amount, not the line item amount!
  
  // Look for other fields that might contain monetary values (including negative)
  for (const [key, value] of Object.entries(item)) {
    // Skip non-numeric fields, hours field, and invoice amount
    if (key === 'hours' || key === 'invoice_amount') continue;
    
    // Handle string values that might have currency symbols (including negative)
    if (typeof value === 'string' && (value.includes('$') || value.includes('-'))) {
      const cleaned = value.replace(/[^\d.-]/g, '');
      const numValue = parseFloat(cleaned);
      if (!isNaN(numValue)) {
        return numValue; // Return negative values for discounts
      }
    } else if (typeof value === 'number') {
      // Return any numeric field, including negative values
      return value;
    }
  }
  
  // If nothing else is found, return 0
  return 0;
};

// Check if an item is a discount
export const isDiscountItem = (item) => {
  // Check if the monetary value is negative
  const amount = extractMonetaryValue(item);
  if (amount < 0) return true;
  
  // Check if description/category indicates it's a discount
  const description = (item.description || '').toLowerCase();
  const category = (item.category || '').toLowerCase();
  const product = (item.product || '').toLowerCase();
  const name = (item.name || '').toLowerCase();
  
  const discountKeywords = ['discount', 'credit', 'refund', 'rebate', 'adjustment'];
  
  return discountKeywords.some(keyword => 
    description.includes(keyword) || 
    category.includes(keyword) || 
    product.includes(keyword) ||
    name.includes(keyword)
  );
};

// Categorize discount items
export const categorizeDiscountItem = (item) => {
  const description = (item.description || '').toLowerCase();
  const category = (item.category || '').toLowerCase();
  
  // Check for specific discount types
  if (description.includes('iaas') || category.includes('iaas')) {
    return 'IaaS Discount';
  }
  if (description.includes('paas') || category.includes('paas')) {
    return 'PaaS Discount';
  }
  if (description.includes('contract')) {
    return 'Contract Discount';
  }
  
  // Default discount category
  return 'Discounts';
};

// Grouping keys. The charts aggregate by these and the line item explorer
// filters by them, so a drill-down always shows exactly the rows behind a bar.
export const getCategoryKey = (item) => {
  if (isDiscountItem(item)) return categorizeDiscountItem(item);
  return item.category || item.name || item.product || item.description || 'Unknown';
};

export const getProductKey = (item) => {
  if (isDiscountItem(item)) return categorizeDiscountItem(item);
  return item.product || item.name || item.type || 'Unknown';
};

export const getProjectKey = (item) => item.project_name || 'Unassigned';

// Used by the "Detailed Line Items" chart, which prefers the product field.
export const getDetailProductKey = (item) =>
  item.product ||
  item.name ||
  item.category ||
  item.group_description ||
  item.description ||
  'Unknown';
